import axios from 'axios';
import { reactive, ref } from 'vue';
import * as Routes from '@/routes';

const state = reactive({
  notifications: [],
  pollingIntervalMs: 30_000,
  pollTimerId: null,
  isFetching: false,
  lastError: null,
});

const unreadCount = ref(0);

async function fetchUnreadCount() {
  try {
    const response = await axios.get(Routes.unread_count_notifications());
    unreadCount.value = response.data?.unread_count || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
  }
}

async function fetchNotifications() {
  if (state.isFetching) return;

  state.isFetching = true;
  try {
    const response = await axios.get(Routes.notifications(), {
      params: { format: 'json' },
    });

    state.notifications = response.data?.notifications || [];
    unreadCount.value = state.notifications.filter((n) => !n.seen).length;
    state.lastError = null;
  } catch (error) {
    state.lastError = error;
  } finally {
    state.isFetching = false;
  }
}

function hydrate(notifications) {
  if (Array.isArray(notifications)) {
    state.notifications = notifications;
    unreadCount.value = state.notifications.filter((n) => !n.seen).length;
  }
}

async function markAllAsSeen() {
  await axios.post(Routes.mark_all_as_seen_notifications());
  state.notifications = state.notifications.map((n) => ({ ...n, seen: true }));
  unreadCount.value = 0;
}

async function markAsSeen(notificationId) {
  await axios.post(Routes.mark_as_seen_notification(notificationId));

  const notification = state.notifications.find((n) => n.id === notificationId);
  if (notification) {
    notification.seen = true;
    unreadCount.value = state.notifications.filter((n) => !n.seen).length;
  }
}

async function deleteNotification(notificationId) {
  await axios.delete(Routes.notification(notificationId));
  state.notifications = state.notifications.filter(
    (n) => n.id !== notificationId,
  );
  unreadCount.value = state.notifications.filter((n) => !n.seen).length;
}

async function deleteAll() {
  await axios.delete(Routes.destroy_all_notifications());
  state.notifications = [];
  unreadCount.value = 0;
}

function startPolling({ intervalMs } = {}) {
  if (state.pollTimerId) return;

  if (typeof intervalMs === 'number' && intervalMs > 0) {
    state.pollingIntervalMs = intervalMs;
  }

  state.pollTimerId = setInterval(() => {
    void fetchUnreadCount();
  }, state.pollingIntervalMs);
}

function stopPolling() {
  if (!state.pollTimerId) return;

  clearInterval(state.pollTimerId);
  state.pollTimerId = null;
}

export default {
  state,
  unreadCount,
  hydrate,
  fetchUnreadCount,
  fetchNotifications,
  startPolling,
  stopPolling,
  markAllAsSeen,
  markAsSeen,
  deleteNotification,
  deleteAll,
};
